import { useState, cloneElement, Children } from "react";
import { cn } from "tools/ui";
import { Column } from "./Column";

function resolveFieldData(data, field) {
  if (!data || !field) return null;

  const parts = field.split(".");
  let current = data;

  for (let i = 0; i < parts.length; i++) {
    if (!current) return null;

    const key = parts[i];

    if (`${key}.` in current) {
      current = current[`${key}.`];
      continue;
    }
    if (key in current) {
      current = current[key];
      continue;
    }
    return null;
  }

  return current;
}

export function DataTable({
  value = [],
  children,
  editable = false,
  onEdit,
  rows = 10,
  paginator = false,
  selectionMode = null, // "single" | "multiple" | null
  selection = [],
  onSelectionChange,
  className = "",
}) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(selection || []);

  function resolveColumnProps(child) {
    if (child.type.columnDef) return child.type.columnDef(child.props);
    if (child.type === Column) return child.props;
    return child.props;
  }

  const columns = Children.toArray(children).map(resolveColumnProps);

  const start = page * rows;
  const end = start + rows;
  const data = paginator ? value.slice(start, end) : value;

  const handleChange = (rowIndex, field, val) => {
    const updated = [...value];
    updated[start + rowIndex][field] = val;
    onEdit?.(updated[start + rowIndex], field, val);
  };

  const isSelected = (row) => {
    const selectedRow =
      selectionMode === "multiple" ? selected.includes(row) : selected === row;

    return selectedRow ? "bg-lime-100" : "";
  };

  const toggleSelection = (row) => {
    let newSelection;
    if (selectionMode === "single") {
      newSelection = selected === row ? null : row;
    } else if (selectionMode === "multiple") {
      newSelection = selected.includes(row)
        ? selected.filter((r) => r !== row)
        : [...selected, row];
    }

    setSelected(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleSelectAll = () => {
    if (selectionMode !== "multiple") return;
    if (selected.length === data.length) {
      setSelected([]);
      onSelectionChange?.([]);
    } else {
      setSelected([...data]);
      onSelectionChange?.(data);
    }
  };

  return (
    <div
      className={`border border-zinc-300 rounded-lg overflow-hidden ${className}`}
    >
      <table className="min-w-full border-collapse text-sm divide-y-2 divide-amber-200 mb-2">
        <thead className="sticky top-0 leading-6 bg-amber-100 text-stone-700">
          <tr>
            {selectionMode === "multiple" && (
              <th className="w-6 px-2">
                <input
                  type="checkbox"
                  checked={selected.length === data.length}
                  onChange={handleSelectAll}
                  className="accent-amber-500 m-0.5 size-3.5"
                />
              </th>
            )}
            {columns.map((col) => (
              <th key={col.field} className="p-2 text-left font-medium">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="text-slate-700">
          {data.map((row, rowIndex) => {
            const selectedRow = isSelected(row);
            return (
              <tr
                key={rowIndex}
                className={cn(
                  "border-b border-zinc-300 odd:bg-white even:bg-zinc-100 hover:bg-amber-50 transition-colors cursor-pointer",
                  selectedRow,
                )}
                onClick={() => (selectionMode ? toggleSelection(row) : null)}
              >
                {selectionMode === "multiple" && (
                  <td className="w-6 px-2">
                    <input
                      type="checkbox"
                      checked={selectedRow}
                      onChange={() => toggleSelection(row)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-amber-500 m-0.5 size-3.5"
                    />
                  </td>
                )}
                {columns.map((col) => {
                  const content =
                    editable && col.editor
                      ? cloneElement(col.editor, {
                          value: resolveFieldData(row, col.field),
                          onChange: (e) =>
                            handleChange(rowIndex, col.field, e.target.value),
                        })
                      : col.body
                        ? col.body(row)
                        : resolveFieldData(row, col.field);

                  return (
                    <td key={col.field} className={cn(col.editor ? "" : "p-3")}>
                      {content}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {paginator && (
        <div className="flex justify-between items-center p-2 text-xs text-gray-600 bg-gray-50">
          <span>
            Página {page + 1} de {Math.ceil(value.length / rows)}
          </span>
          <div className="space-x-1">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="px-2 py-1 border rounded disabled:opacity-40"
            >
              ←
            </button>
            <button
              type="button"
              disabled={end >= value.length}
              onClick={() => setPage((p) => p + 1)}
              className="px-2 py-1 border rounded disabled:opacity-40"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
